import { UserFragmentFragment } from "../../gql/graphql.ts";
import { formatDateTime } from "../../UI/Utils/formatDate.ts";
import { useConfirm } from "../../UI/Feedback/useConfirm.ts";
import { Card } from "../../UI/Card.tsx";
import { FeedbackReportScore } from "./FeedbackReportScore.tsx";
import { TrashIcon } from "@heroicons/react/24/outline/index.js";
import { useDeleteLessonFeedback } from "./useDeleteLessonFeedback.ts";
import { PrimaryLoadingIndicator } from "../../UI/Loading/PrimaryLoadingIndicator.tsx";
import { LoaderSize } from "../../UI/Loading/LoaderSize.tsx";

type Props = {
  user: UserFragmentFragment;
  onFeedbackDeleted: () => void;
};
export function UserLessonFeedbackComponent({
  user,
  onFeedbackDeleted,
}: Props) {
  const { deleteLessonFeedback, loading } = useDeleteLessonFeedback();
  const askConfirmation = useConfirm();
  function formatDate(dateString: string): string {
    return formatDateTime(dateString, { withWeekday: true });
  }

  async function deleteFeedback(feedbackId: string) {
    if (
      await askConfirmation({
        title: "Delete this feedback?",
        message: "This action cannot be undone.",
        confirmLabel: "Delete",
        isDestructive: true,
      })
    ) {
      await deleteLessonFeedback(feedbackId);
      onFeedbackDeleted();
    }
  }

  const showedFeedbacks = user.lessonFeedbacks.filter((lf) => !lf.noShow);

  const avgParticipation =
    showedFeedbacks.length <= 0
      ? 0
      : showedFeedbacks.reduce((acc, lf) => acc + lf.participation, 0) /
        showedFeedbacks.length;

  const avgTargetLanguage =
    showedFeedbacks.length <= 0
      ? 0
      : showedFeedbacks.reduce((acc, lf) => acc + lf.targetLanguage, 0) /
        showedFeedbacks.length;
  return (
    <div className={"space-y-4"}>
      <Card>
        <FeedbackReportScore
          label={"Avg Participation"}
          value={avgParticipation}
        />
      </Card>
      <Card>
        <FeedbackReportScore
          label={"Avg Target language"}
          value={avgTargetLanguage}
        />
      </Card>
      {user.lessonFeedbacks.map((lf, i) => (
        <Card key={i}>
          <div>
            <div className={"flex justify-between"}>
              <div className={"flex items-center gap-2"}>
                <p>
                  Taken on <b>{formatDate(lf.classRoomStartDateUtc)}</b> with
                  teacher{" "}
                  <b>
                    {lf.teacher.givenName} {lf.teacher.familyName}
                  </b>
                </p>
                <span
                  className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${lf.isOneOnOne ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}
                >
                  {lf.isOneOnOne ? "1 on 1" : "Group"}
                </span>
              </div>
              {loading ? (
                <PrimaryLoadingIndicator size={LoaderSize.Small} />
              ) : (
                <div
                  className={"ml-2 cursor-pointer"}
                  onClick={async () => {
                    await deleteFeedback(lf.id);
                  }}
                >
                  <TrashIcon className={"w-5 text-red-600"} />
                </div>
              )}
            </div>
            {lf.noShow ? (
              <p className={"text-red-600 font-bold"}>No Show</p>
            ) : (
              <div>
                <p>Participation: {lf.participation}</p>
                <p>Target language: {lf.targetLanguage}</p>
                {lf.notes ? <p>Notes: {lf.notes}</p> : null}
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
